/**
 * Link Hover Color
 * ホバー時にランダムでカラーを変更するコンポーネント
 *
 * 3 色の dark / light ペア (red / green / blue) からランダムに選択し、
 * 連続で同じ色にならない。 dark / light を **同期** で body に setProperty
 * するため、 text には dark 系、 background には light 系 (同じ色相) を
 * 一貫して適用できる。
 *
 * 対象属性:
 * - data-hover-color → mouseenter/touchstart/focus で body の --rkd-color-* を更新
 *
 * CSS 変数 (rkd- prefix = rakuda の子音省略形、 ブランド namespace):
 * - --rkd-color-dark : dark 系 (text 推奨)
 * - --rkd-color      : light 系 (background 推奨)
 */
export class LinkHoverColor {
  /**
   * @param {HTMLElement|null} element - 初期化対象のコンテナ要素
   */
  constructor(element = null) {
    this.element = element || document.body;

    /** @type {Array<{dark: string, light: string}>} */
    this.colorPairs = [
      { dark: 'var(--color-dark-red)', light: 'var(--color-red)' },
      { dark: 'var(--color-dark-green)', light: 'var(--color-green)' },
      { dark: 'var(--color-dark-blue)', light: 'var(--color-blue)' },
    ];

    /** @type {{dark: string, light: string}|null} 直前のペアを管理 */
    this.lastPair = null;

    /** @type {(e: MouseEvent) => void} */
    this.handleMouseEnter = this.handleMouseEnter.bind(this);

    /** @type {(e: TouchEvent) => void} */
    this.handleTouchStart = this.handleTouchStart.bind(this);

    /** @type {(e: FocusEvent) => void} */
    this.handleFocus = this.handleFocus.bind(this);

    this.init();
  }

  /**
   * 初期化処理
   * @returns {void}
   */
  init() {
    if (!this.element) return;
    this.setInitialColor();
    this.setupEventListeners();
  }

  /**
   * 初期カラーをランダムに設定
   * @returns {void}
   */
  setInitialColor() {
    const pair = this.getRandomColorPair();
    this.lastPair = pair;
    this._setVars(pair);
  }

  /**
   * イベントリスナーを設定（イベント委譲・キャプチャフェーズ）
   * @returns {void}
   */
  setupEventListeners() {
    this.element.addEventListener('mouseenter', this.handleMouseEnter, true);
    this.element.addEventListener('touchstart', this.handleTouchStart, true);
    // focus は bubble しないので capture phase で listen。 :focus-visible 判定で tab フォーカスのみ trigger
    this.element.addEventListener('focus', this.handleFocus, true);
  }

  /**
   * マウスエンター時の処理
   * @param {MouseEvent} e - マウスイベント
   * @returns {void}
   */
  handleMouseEnter(e) {
    const target = /** @type {Element} */ (e.target);
    const el = target.closest('[data-hover-color]');
    if (!(el instanceof HTMLElement)) return;

    // el内部からの移動（子要素間の移動）は無視
    if (el.contains(/** @type {Node} */ (e.relatedTarget))) return;

    this._applyColor();
  }

  /**
   * タッチ開始時の処理
   * @param {TouchEvent} e - タッチイベント
   * @returns {void}
   */
  handleTouchStart(e) {
    const target = /** @type {Element} */ (e.target);
    const el = target.closest('[data-hover-color]');
    if (!(el instanceof HTMLElement)) return;

    this._applyColor();
  }

  /**
   * フォーカス時の処理 (tab フォーカスのみ trigger)
   * @param {FocusEvent} e - フォーカスイベント
   * @returns {void}
   */
  handleFocus(e) {
    const target = /** @type {Element} */ (e.target);
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches(':focus-visible')) return;
    const el = target.closest('[data-hover-color]');
    if (!(el instanceof HTMLElement)) return;

    this._applyColor();
  }

  /**
   * ランダムなペアを body に適用
   * @returns {void}
   */
  _applyColor() {
    const pair = this.getRandomColorPair();
    this.lastPair = pair;
    this._setVars(pair);
  }

  /**
   * ペアを body の CSS 変数として書き込む
   * @param {{dark: string, light: string}} pair
   * @returns {void}
   */
  _setVars(pair) {
    document.body.style.setProperty('--rkd-color-dark', pair.dark);
    document.body.style.setProperty('--rkd-color', pair.light);
  }

  /**
   * 直前と異なるランダムなペアを取得
   * @returns {{dark: string, light: string}} 選択されたペア
   */
  getRandomColorPair() {
    const available = this.colorPairs.filter(p => p !== this.lastPair);
    return available[Math.floor(Math.random() * available.length)];
  }

  /**
   * クリーンアップ処理
   * @returns {void}
   */
  destroy() {
    if (this.element) {
      this.element.removeEventListener(
        'mouseenter',
        this.handleMouseEnter,
        true,
      );
      this.element.removeEventListener(
        'touchstart',
        this.handleTouchStart,
        true,
      );
      this.element.removeEventListener('focus', this.handleFocus, true);
    }
  }
}
